/**
 * @owner @Appboy/frontend-ix
 */

import sharedConstants from "@lib/shared_constants";

/**
 * Modern `/src` home for the static (non-feature-flipper-gated) members of the
 * legacy `lib/constants/liquid-template-constants` config object.
 *
 * Feature-flipper-gated members do NOT live here — they are consumed reactively
 * via hooks instead:
 * - the feature-flipper-gated `TEMPLATE_VARIABLE_REGEXES` members (device-attribute
 *   and WhatsApp regexes, which filter by `new_ad_ids` / `whats_app_inbound_*`)
 *   -> `useLiquidTemplateVariableRegexes` (@services)
 * - `ATTRIBUTES_WITH_BRACES` / `WHITELISTED_VALUES` -> `useLiquidAttributesWithBraces` (@services)
 *
 * The `*_VARIABLE_REGEX` exports below are the *static* `TEMPLATE_VARIABLE_REGEXES`
 * members — they derive only from `NAMESPACES` and non-feature-flipper-gated whitelists,
 * so they carry no reactive behavior and are safe to consume as plain constants.
 */

export const NAMESPACES = sharedConstants.LIQUID_CONSTANTS.NAMESPACES;

export const CONTENT_BLOCKS = sharedConstants.LIQUID_CONSTANTS.CONTENT_BLOCKS;

export const SPECIAL_TAGS = sharedConstants.LIQUID_CONSTANTS.SPECIAL_TAGS;

export const LIQUID_PARTS = {
  OBJECT_START: "{{",
  OBJECT_END: "}}",
  TAG_START: "{%",
  // n.b. this was TAG_START, I can only assume this was a long-suffering typo?
  TAG_END: "%}",
  VARIABLE_START: "${",
  VARIABLE_END: "}",
};

const { OBJECT_START, OBJECT_END, TAG_START, TAG_END, OBJECT, TAG, TAG_OR_OBJECT } = sharedConstants.LIQUID.REGEXES;

export const LIQUID_REGEXES = {
  OBJECT_START: new RegExp(OBJECT_START, "g"),
  OBJECT_END: new RegExp(OBJECT_END, "g"),
  TAG_START: new RegExp(TAG_START, "g"),
  TAG_END: new RegExp(TAG_END, "g"),
  OBJECT: new RegExp(OBJECT, "g"),
  TAG: new RegExp(TAG, "g"),
  TAG_OR_OBJECT: new RegExp(TAG_OR_OBJECT, "g"),
};

export const COMPLETION_NAMESPACES: {
  [K in keyof typeof NAMESPACES]: string;
} = Object.fromEntries(
  Object.entries(NAMESPACES).map(([key, namespace]) => {
    return [key, `${LIQUID_PARTS.OBJECT_START}${namespace}.${LIQUID_PARTS.VARIABLE_START}`];
  })
) as {
  [K in keyof typeof NAMESPACES]: string;
};

// Trailing lookahead shared by the template-variable regexes below: a `}` that is
// eventually followed by a liquid tag-end or object-end token.
const endRegex = new RegExp(`\\}(?=.*${LIQUID_REGEXES.TAG_END.source}|.*${LIQUID_REGEXES.OBJECT_END.source})`, "g");

export const CONTEXT_PROPERTIES_VARIABLE_REGEX = new RegExp(
  `(?:${NAMESPACES.CONTEXT_PROPERTIES}|${NAMESPACES.CANVAS_ENTRY_PROPERTIES})\\.\\$\\{(.+?)${endRegex.source}`,
  "g"
);

export const EVENT_PROPERTIES_VARIABLE_REGEX = new RegExp(
  `${NAMESPACES.EVENT_PROPERTIES}\\.\\$\\{(.+?)${endRegex.source}`,
  "g"
);

export const API_TRIGGER_EVENT_PROPERTIES_VARIABLE_REGEX = new RegExp(
  `${NAMESPACES.API_TRIGGER_EVENT_PROPERTIES}\\.\\$\\{(.+?)${endRegex.source}`,
  "g"
);

export const SMS_PROPERTIES_VARIABLE_REGEX = new RegExp(
  `${NAMESPACES.SMS_PROPERTIES}\\.\\$\\{(${sharedConstants.LIQUID_CONSTANTS.WHITELISTED_VALUES.SMS_PROPERTIES.join(
    "|"
  )})${endRegex.source}`,
  "g"
);

export const WHATS_APP_PROPERTIES_VARIABLE_REGEX = new RegExp(
  `${NAMESPACES.WHATS_APP_PROPERTIES}\\.\\$\\{(${sharedConstants.LIQUID_CONSTANTS.WHITELISTED_VALUES.WHATS_APP_PROPERTIES.join(
    "|"
  )})${endRegex.source}`,
  "g"
);
